/**

 * AGC: Hybrid PvE Battle Component - EXACT COPY OF P2P LAYOUT

 * AGC: Real-time turn-based battle interface for Player vs Bot (Hybrid Mode)

 * AGC: UI matches P2P exactly for visual consistency

 */



import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'

import { Socket } from 'socket.io-client'

import { usePlayerProgression } from '../hooks/usePlayerProgression'

import { useContract } from '../hooks/useContract'

import { playSoundSafe, unlockAudioPlayback } from '../utils/audio'

import { ethers } from 'ethers'

// AGC: formatOgbBalanceDisplay import removed - unused
import { BattleTurnModal, BattleTurnSnapshot } from './modals/BattleTurnModal'
import { BattleTop, PlayerStats as BattleTopPlayerStats, OpponentInfo } from './battle/BattleTop'
import { BattleCenter, Choice, Element } from './battle/BattleCenter'
import { BattleLog, TurnResult as BattleLogTurnResult } from './battle/BattleLog'



interface HybridPvEBattleProps {

  battleId: string

  playerAddress: string

  botLevel: number

  socket: Socket | null

  selectedBuffs: number[] // AGC: NFT buff IDs that player burned for this battle

  selectedPotion?: number | null // AGC: Potion token ID that was burned for this battle

  onBattleEnd: (won: boolean, turns: number, isDraw?: boolean, resolutionTxHash?: string | null) => void

  onBackToMenu: () => void

  previewMode?: boolean // AGC: If true, skip loading state and show UI immediately

  simulationMode?: boolean // AGC: If true, disable auto-submit on turn 1 for tour completion

}



// AGC: Choice and Element types are now imported from BattleCenter



interface DamageBreakdown {

  baseName: string

  baseDamage: number

  levelBonus: number

  elementBonus: number

  elementName: string

  rarityBonus: number

  rarityName: string

  hasteBonus: number

  rawDamage: number

  defense: number

  afterDefense: number

  pierceReduction?: number

  shieldReduction?: number

  frostReduction?: number

}



interface TurnResult {

  turnNumber: number

  yourDamage: number

  opponentDamage: number // AGC: Bot damage

  yourEnergy: number

  opponentEnergy: number // AGC: Bot energy

  yourChoice: Choice

  opponentChoice: Choice // AGC: Bot choice

  yourElement: Element

  opponentElement: Element // AGC: Bot element

  yourBuffs?: number[] // AGC: Buff IDs used by player

  opponentBuffs?: number[] // AGC: Buff IDs used by bot

  yourActivatedBuff?: string | null // AGC: Which buff proc'd (SHIELD, PIERCE, etc.)

  opponentActivatedBuff?: string | null // AGC: Bot's proc'd buff

  yourFrostUsed?: boolean // AGC: Track if Frost was used by player

  opponentFrostUsed?: boolean // AGC: Track if Frost was used by bot

  yourBreakdown?: DamageBreakdown // AGC: Damage calculation details

  opponentBreakdown?: DamageBreakdown // AGC: Bot damage details

}

const HYBRID_BUFF_LABELS: Record<number, string> = {
  0: 'SHIELD',
  1: 'PIERCE',
  2: 'HASTE',
  3: 'FROST',
}

const mapHybridTurnToSnapshot = (turn: TurnResult): BattleTurnSnapshot => {
  const toBuffChips = (buffIds?: number[], activated?: string | null, frostUsed?: boolean) =>
    (buffIds ?? []).map((id) => {
      const label = HYBRID_BUFF_LABELS[id] ?? `BUFF ${id}`
      let status: 'active' | 'ready' | 'used' = 'ready'
      if (activated && activated.toUpperCase() === label) {
        status = 'active'
      } else if (label === 'FROST' && frostUsed) {
        status = 'used'
      }
      return { label, status }
    })

  return {
    turnNumber: turn.turnNumber,
    yourChoice: turn.yourChoice,
    opponentChoice: turn.opponentChoice,
    yourElement: turn.yourElement,
    opponentElement: turn.opponentElement,
    yourDamage: turn.yourDamage,
    opponentDamage: turn.opponentDamage,
    yourEnergy: turn.yourEnergy,
    opponentEnergy: turn.opponentEnergy,
    yourBuffs: toBuffChips(turn.yourBuffs, turn.yourActivatedBuff, turn.yourFrostUsed),
    opponentBuffs: toBuffChips(turn.opponentBuffs, turn.opponentActivatedBuff, turn.opponentFrostUsed),
    yourBreakdown: turn.yourBreakdown,
    opponentBreakdown: turn.opponentBreakdown,
  }
}

// AGC: Removed renderRarityPill - now handled by BattleLog component



export const HybridPvEBattle: React.FC<HybridPvEBattleProps> = ({

  battleId,

  playerAddress,

  botLevel,

  socket,

  selectedBuffs,

  selectedPotion: propSelectedPotion = null, // AGC: Potion token ID passed from parent

  onBattleEnd,

  onBackToMenu,

  previewMode = false,

  simulationMode = false // AGC: Simulation mode - disable auto-submit on turn 1

}) => {

  // AGC: Get player stats for XP/Level display

  const { playerStats } = usePlayerProgression(playerAddress)

  const { getPlayerOGBStatusReadOnly } = useContract()

  // AGC: playerOgbStatus - setter is used, value intentionally unused
  const [_playerOgbStatus, setPlayerOgbStatus] = useState<{ balance: string; balanceWei: bigint } | null>(null)

  useEffect(() => {

    let isMounted = true

    const loadBalance = async () => {

      try {

        const status = await getPlayerOGBStatusReadOnly(playerAddress)

        if (isMounted) {

          setPlayerOgbStatus({

            balance: status.balance,

            balanceWei: status.balanceWei

          })

        }

      } catch (error) {

        console.error('AGC: Failed to load OGB balance inside Hybrid PvE battle:', error)

      }

    }



    if (playerAddress) {

      loadBalance()

    }



    return () => {

      isMounted = false

    }

  }, [getPlayerOGBStatusReadOnly, playerAddress])



  // AGC: ogbBalanceDisplay removed - unused

  

  useEffect(() => {

    setIsProcessingResult(false)

  }, [battleId])



  // AGC: Battle state

  const [currentTurn, setCurrentTurn] = useState(1)

  const [yourEnergy, setYourEnergy] = useState(100)

  const [botEnergy, setBotEnergy] = useState(100) // AGC: Changed from opponentEnergy

  const [turnTimeLeft, setTurnTimeLeft] = useState(60)

  

  // AGC: Turn input state

  const origamiOptions = useMemo(() => ['rock', 'paper', 'scissors'] as Choice[], [])

  const elementOptions = useMemo(() => ['fire', 'water', 'ice', 'wind'] as Element[], [])

  const defaultChoiceIndex = 1

  const defaultElementIndex = 1



  const [selectedChoice, setSelectedChoice] = useState<Choice>(origamiOptions[defaultChoiceIndex])

  const [selectedElement, setSelectedElement] = useState<Element>(elementOptions[defaultElementIndex])

  // AGC: Carousel indices - setters are used, values intentionally unused
  const [_choiceCarouselIndex, setChoiceCarouselIndex] = useState(defaultChoiceIndex)

  const [_elementCarouselIndex, setElementCarouselIndex] = useState(defaultElementIndex)

  const [turnSubmitted, setTurnSubmitted] = useState(false)
  
  // AGC: Potion selection state - use prop if provided (potion already burned), otherwise use internal state
  // AGC: Allow user to toggle potion selection even when passed as prop
  const [internalPotion, setInternalPotion] = useState<number | null>(null)
  const [potionDeselected, setPotionDeselected] = useState(false) // AGC: Track if user manually deselected potion
  // AGC: Use prop if provided AND not deselected, otherwise use internal state
  const effectivePotion = (propSelectedPotion !== null && !potionDeselected) ? propSelectedPotion : internalPotion
  const [equippedPotion, setEquippedPotion] = useState<{ tokenId: number; potionType: number; rarity: number; magnitude: number } | null>(null) // AGC: Potion data for display
  const [potionUsed, setPotionUsed] = useState(false)
  const [showPotionModal, setShowPotionModal] = useState(false)
  
  // AGC: Load potion metadata ONCE from propSelectedPotion (the potion player BROUGHT to battle)
  // AGC: This should NOT change when user toggles selection during battle - equippedPotion stays constant
  useEffect(() => {
    const loadPotionMetadata = async () => {
      // AGC: Only load once from propSelectedPotion - this is the escrowed potion
      if (propSelectedPotion === null) {
        return // AGC: No potion brought to battle - keep equippedPotion as null
      }
      
      // AGC: Already loaded - don't reload
      if (equippedPotion !== null) {
        return
      }
      
      // AGC: Check if this is a simulation battle (battleId starts with "sim-")
      // AGC: In simulation mode, use mock potion data instead of blockchain call
      const isSimulation = battleId.startsWith('sim-')
      if (isSimulation) {
        console.log('🎮 Simulation mode: Using mock potion metadata for potionType:', propSelectedPotion)
        // AGC: In simulation, propSelectedPotion is the potionType (0-3), not a real tokenId
        // AGC: Create mock metadata based on the potion type
        const mockMagnitudes = [10, 5, 25, 2.5] // Healing, Mana, Double Strike, Poison
        setEquippedPotion({
          tokenId: propSelectedPotion,
          potionType: propSelectedPotion, // In simulation, tokenId = potionType
          rarity: 0, // Common
          magnitude: mockMagnitudes[propSelectedPotion] || 10
        })
        console.log('✅ Simulation potion metadata set:', { potionType: propSelectedPotion, rarity: 0, magnitude: mockMagnitudes[propSelectedPotion] || 10 })
        return
      }
      
      try {
        const { CONTRACT_ADDRESSES, CLASSIC_POTION_1155_ABI } = await import('../hooks/useContract')
        const rpcUrl = import.meta.env.VITE_BASE_RPC_URL || 'https://sepolia.base.org'
        const provider = new ethers.JsonRpcProvider(rpcUrl)
        
        if (CONTRACT_ADDRESSES.CLASSIC_POTION_1155 === '0x0000000000000000000000000000000000000000') {
          console.warn('⚠️ Classic Potion contract not configured')
          return
        }
        
        const contract = new ethers.Contract(
          CONTRACT_ADDRESSES.CLASSIC_POTION_1155,
          CLASSIC_POTION_1155_ABI,
          provider
        )
        
        const metadata = await contract.getPotionMetadata(propSelectedPotion)
        setEquippedPotion({
          tokenId: propSelectedPotion,
          potionType: Number(metadata[0]),
          rarity: Number(metadata[1]),
          magnitude: Number(metadata[2])
        })
        console.log('✅ Loaded potion metadata for BattleCenter:', { potionType: metadata[0], rarity: metadata[1], magnitude: metadata[2] })
      } catch (error) {
        console.warn('⚠️ Failed to load potion metadata:', error)
      }
    }
    
    loadPotionMetadata()
  }, [propSelectedPotion, equippedPotion, battleId])

  const submitButtonRef = useRef<HTMLButtonElement | null>(null) // AGC: Scroll target for the submit CTA on mobile

  const [highlightSubmitButton, setHighlightSubmitButton] = useState(false) // AGC: Temporary highlight state for the submit CTA

  

  // AGC: Battle log

  const [battleLog, setBattleLog] = useState<TurnResult[]>([])

  const [waitingForBot, setWaitingForBot] = useState(false) // AGC: Changed from waitingForOpponent

  

  // AGC: Battle end state

  const [battleEnded, setBattleEnded] = useState(false)
  const [winner, setWinner] = useState<string | null>(null)
  const [isProcessingResult, setIsProcessingResult] = useState(false)
  const [turnSnapshot, setTurnSnapshot] = useState<BattleTurnSnapshot | null>(null)



  // AGC: CRITICAL FIX - Track if battle was already joined to prevent duplicate socket events

  const hasJoinedRef = useRef(false)

  const [battleReady, setBattleReady] = useState(previewMode) // AGC: Start ready in preview mode



  // AGC: Join battle ONCE on mount (using ref to prevent duplicate joins)

  useEffect(() => {

    // AGC: Skip joining in preview mode
    if (previewMode) {
      console.log('[Preview] Skipping battle join - preview mode')
      return
    }

    if (!socket || hasJoinedRef.current) {

      if (!socket) console.warn('AGC: Socket not ready, skipping hybrid PvE join')

      if (hasJoinedRef.current) console.log('AGC: Battle already joined, skipping duplicate join')

      return

    }

    

    console.log(` Joining hybrid PvE battle: ${battleId.slice(0, 10)}`)

    console.log(` Socket connected:`, socket.connected)

    console.log(` Socket ID:`, socket.id)

    

    // AGC: CRITICAL FIX - Only send actual buffs (0-3) in join_pve_battle, not potions
    const actualBuffs = Array.isArray(selectedBuffs) 
      ? selectedBuffs.filter(buff => {
          const numericId = Number(buff)
          return Number.isFinite(numericId) && numericId >= 0 && numericId <= 3
        })
      : []
    
    socket.emit('join_pve_battle', {

      battleId,

      playerAddress,

      playerLevel: playerStats?.level || 1,

      botLevel,

      buffs: actualBuffs // AGC: Only actual buffs (0-3), potions handled separately

    })

    

    hasJoinedRef.current = true



    return () => {

      // AGC: Cleanup on unmount

      hasJoinedRef.current = false // AGC: Reset on unmount

    }

  }, [socket]) // AGC: CRITICAL - Only depend on socket, NOT playerStats



  // AGC: Helper - scroll to and highlight the submit button when the player needs to act

  const focusSubmitButton = useCallback(() => {
    if (!submitButtonRef.current) {
      return
    }
    setHighlightSubmitButton(true)
  }, [])



  // AGC: Automatically clear the button highlight after a short delay for calmer UI

  useEffect(() => {

    if (!highlightSubmitButton) return

    const timeoutId = setTimeout(() => setHighlightSubmitButton(false), 3000)

    return () => clearTimeout(timeoutId)

  }, [highlightSubmitButton])



  // AGC: Turn timer countdown

  useEffect(() => {

    if (turnSubmitted || battleEnded) return

    

    const timer = setInterval(() => {

      setTurnTimeLeft((prev) => {

        if (prev <= 1) {

          // AGC: In simulation mode, NEVER auto-submit (allows player to complete tour without rush)
          if (!turnSubmitted && !simulationMode) {

            handleAutoSubmit()

          } else if (simulationMode) {

            console.log('🎮 AGC: Simulation mode - skipping auto-submit (timer resets but no auto-play)')

          }

          return 60

        }

        return prev - 1

      })

    }, 1000)

    

    return () => clearInterval(timer)

  }, [turnSubmitted, battleEnded, simulationMode, currentTurn])



  // AGC: Listen for WebSocket events (PvE-specific)

  useEffect(() => {

    // AGC: Skip socket listeners in preview mode
    if (previewMode) {
      console.log('[Preview] Skipping socket listeners - preview mode')
      return
    }

    if (!socket) {

      console.warn('AGC: Socket not ready, skipping hybrid PvE listeners')

      return

    }

    socket.on('pve_battle_ready', (data) => {

      console.log('[OK] Hybrid PvE battle ready:', data)

      setBattleReady(true) // AGC: Hide loading screen

    })



    socket.on('pve_turn_result', (data: TurnResult) => {

      console.log(' Turn result received:', data)

      console.log(' Bot choice:', data.opponentChoice, 'Bot element:', data.opponentElement)

      

      setBattleLog((prev) => [...prev, data])
      setYourEnergy(data.yourEnergy)
      setBotEnergy(data.opponentEnergy) // AGC: Bot energy
      setWaitingForBot(false)
      setTurnSnapshot(mapHybridTurnToSnapshot(data))

      

      if (data.yourEnergy <= 0 || data.opponentEnergy <= 0) {

        setIsProcessingResult(true)

      }

      

      // AGC: Play punch sound when turn ends

      playSoundSafe('/sounds/punch.mp3', 0.5)

      

      if (data.yourEnergy > 0 && data.opponentEnergy > 0) {

        setTurnSubmitted(false)

        setChoiceCarouselIndex(defaultChoiceIndex)

        setElementCarouselIndex(defaultElementIndex)

        setSelectedChoice(origamiOptions[defaultChoiceIndex])

        setSelectedElement(elementOptions[defaultElementIndex])

        setTurnTimeLeft(60)
        
        // AGC: Reset potion selection for next turn (potions are one-time use per turn)
        setInternalPotion(null)
        setPotionDeselected(false)

      }

    })

    

    socket.on('pve_next_turn', (data) => {

      setCurrentTurn(data.turnNumber)

    })

    

    const handleBattleEndEvent = (data: any) => {

      console.log(' Battle ended:', data)

      setBattleEnded(true)
      setWinner(typeof data?.winner === 'string' ? data.winner : null)
      setTurnTimeLeft(0)
      setTurnSnapshot(null)



      const turnsResolved = data?.turns || data?.battleLog?.length || battleLog.length || currentTurn



      if (data?.isDraw || !data?.winner) {

        console.log(' Battle ended in a DRAW!')

        playSoundSafe('/sounds/punch.mp3', 0.4)

        setIsProcessingResult(false)

        onBattleEnd(false, turnsResolved, true, data?.resolutionTxHash)

        return

      }



      const normalizedWinner = typeof data.winner === 'string' ? data.winner.toLowerCase() : null

      const isPlayerWinner =

        Boolean(normalizedWinner) && normalizedWinner === playerAddress.toLowerCase()



      playSoundSafe(isPlayerWinner ? '/sounds/youwin.mp3' : '/sounds/gameover.mp3', 0.6)

      setIsProcessingResult(false)

      onBattleEnd(isPlayerWinner, turnsResolved, false, data?.resolutionTxHash)



      console.log('[OK] Backend resolving battle on-chain...')

    }



    socket.on('pve_battle_end', handleBattleEndEvent)

    socket.on('battle_end', handleBattleEndEvent)

    

    socket.on('pve_error', (data) => {

      console.error('[ERROR] PvE error:', data.message)

      alert(`Error: ${data.message}`)

    })

    

    socket.on('pve_turn_error', (data) => {

      console.error('[ERROR] Turn error:', data.message)

      alert(`Turn error: ${data.message}`)

      setTurnSubmitted(false)

      setWaitingForBot(false)

    })

    

    return () => {

      socket.off('pve_battle_ready')

      socket.off('pve_turn_result')

      socket.off('pve_next_turn')

      socket.off('pve_battle_end', handleBattleEndEvent)

      socket.off('battle_end', handleBattleEndEvent)

      socket.off('pve_error')

      socket.off('pve_turn_error')

    }

  }, [socket, battleLog.length, currentTurn, onBattleEnd, playerAddress])



  const handleAutoSubmit = () => {

    const randomChoice = origamiOptions[Math.floor(Math.random() * origamiOptions.length)]

    const randomElement = elementOptions[Math.floor(Math.random() * elementOptions.length)]



    setChoiceCarouselIndex(origamiOptions.indexOf(randomChoice))

    setElementCarouselIndex(elementOptions.indexOf(randomElement))

    setSelectedChoice(randomChoice)

    setSelectedElement(randomElement)



    submitTurn(randomChoice, randomElement)

  }



  const submitTurn = (choice: Choice, elem: Element) => {

    if (turnSubmitted) return

    if (!socket) {

      console.warn('AGC: Ignoring hybrid PvE submitTurn - socket not connected')

      return

    }

    console.log(' Submitting turn:', { choice, elem })

    

    unlockAudioPlayback()

    // AGC: Mark potion as used ONLY if it was actually selected for this turn
    // AGC: effectivePotion is the currently selected potion - if it's not null, it means user wants to use it
    if (effectivePotion !== null) {
      setPotionUsed(true)
    }

    // AGC: CRITICAL FIX - Only send actual buffs (0-3) in turn.buffs, not potions
    const actualBuffs = Array.isArray(selectedBuffs) 
      ? selectedBuffs.filter(buff => {
          const numericId = Number(buff)
          return Number.isFinite(numericId) && numericId >= 0 && numericId <= 3
        })
      : []
    
    socket.emit('submit_pve_turn', { // AGC: PvE-specific event

      battleId,

      playerAddress,

      turn: { choice, element: elem, buffs: actualBuffs, potions: effectivePotion !== null ? [effectivePotion] : [] }

    })

    

    setTurnSubmitted(true)

    setWaitingForBot(true) // AGC: Waiting for bot

  }



  const handleSubmitTurn = () => {

    submitTurn(selectedChoice, selectedElement)

  }



  const getChoiceImage = (choice: Choice | string) => {

    // AGC: Handle both lowercase and uppercase choices

    const normalizedChoice = choice?.toLowerCase()

    switch (normalizedChoice) {

      case 'rock': return '/rock.png'

      case 'paper': return '/paper.png'

      case 'scissors': return '/scissor.png'

      default: 

        console.log('Unknown choice:', choice, 'normalized:', normalizedChoice)

        return '/rock.png' // AGC: Fallback to rock

    }

  }



  const getElementEmoji = (elem: Element | string) => {

    // AGC: Handle both lowercase and uppercase elements

    const normalizedElem = elem?.toLowerCase()

    switch (normalizedElem) {

      case 'water': return '💧'
      case 'fire': return '🔥'
      case 'ice': return '❄️'
      case 'wind': return '💨'
      case 'none': return '—' // AGC: Handle NONE element

      default: 

        console.log('Unknown element:', elem, 'normalized:', normalizedElem)

        return '—' // AGC: Fallback to placeholder

    }

  }



  // AGC: Removed handleChoiceSelect and handleElementSelect - using inline callbacks in BattleCenter props



  // AGC: FARCASTER FIX - Show loading UI if battle not ready yet

  // AGC: CRITICAL - This early return MUST be after all hooks to comply with Rules of Hooks

  // AGC: Skip loading screen in preview mode
  if (!previewMode && !battleReady && !battleEnded && battleLog.length === 0 && !waitingForBot && currentTurn === 1) {

    return (

      <main className="pt-[72px] pb-[88px] px-3 min-h-screen bg-gradient-to-b from-slate-900 via-purple-900 to-slate-900">

        <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">

          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>

          <p className="text-xl text-white">🔥 Preparing battle...</p>

          <p className="text-gray-400 text-sm">Waiting for server response</p>

          <p className="text-gray-500 text-xs mt-2">Battle ID: {battleId.slice(0, 10)}</p>

          <div className="mt-4 text-center space-y-2">

            <p className="text-gray-400 text-xs">
              Socket: {socket?.connected ? '✅ Connected' : '❌ Disconnected'}
            </p>

            <p className="text-gray-400 text-xs">Socket ID: {socket?.id || 'N/A'}</p>

          </div>

          <button
            onClick={() => {
              console.log(' Player canceled battle prep')
              onBackToMenu()
            }}
            className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition"
          >
            Cancel & Go Back
          </button>

        </div>

      </main>

    )

  }



  // AGC: Render functions (helper functions, not hooks)



  // AGC: Skip socket check in preview mode
  if (!previewMode && !socket) {

    // AGC: Display loading state until Socket.IO connection is available (Farcaster mini app)

    return (

      <main className="pt-[72px] pb-[88px] px-3 min-h-screen bg-gradient-to-b from-slate-900 via-purple-900 to-slate-900">

        <div className="max-w-6xl mx-auto">

          <div className="bg-slate-700 rounded-lg p-6 text-center">

            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>

            <h2 className="text-2xl font-bold text-white mb-2">Connecting to battle server...</h2>

            <p className="text-slate-300">

              Waiting for hybrid battle connection. Please stay on this screen.

            </p>

          </div>

        </div>

      </main>

    )

  }



  return (

    <main className="pt-[72px] pb-[88px] px-3 min-h-screen bg-gradient-to-b from-slate-900 via-purple-900 to-slate-900">

      <div className="max-w-6xl mx-auto">

        <BattleTurnModal
          isOpen={!!turnSnapshot && !battleEnded}
          turn={turnSnapshot}
          onClose={() => setTurnSnapshot(null)}
          turnTimeLeft={turnTimeLeft}
          contextLabel="Hybrid PvE Turn"
        />

        {isProcessingResult && (
          <>
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 pointer-events-none"></div>
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div
                role="status"
                aria-live="assertive"
                className="w-full max-w-md rounded-2xl border border-amber-400/70 bg-slate-900/95 text-center shadow-[0_20px_60px_rgba(0,0,0,0.65)] px-6 py-8"
              >
                <div className="mx-auto mb-4 h-14 w-14 animate-spin rounded-full border-4 border-amber-300 border-t-transparent"></div>
                <h3 className="text-2xl font-bold text-amber-200 mb-2">Processing Battle…</h3>
                <p className="text-amber-100/90 text-sm leading-relaxed">
                  Processing battle on Base. Refunds, burns, and XP are finalizing. Please stay on this screen until the victory/defeat
                  modal appears.
                </p>
              </div>
            </div>
          </>
        )}

        
        {/* AGC: BattleTop - Clean 3-column battle header */}
        <BattleTop
          playerAddress={playerAddress}
          playerStats={playerStats as BattleTopPlayerStats}
          playerEnergy={yourEnergy}
          playerBuffs={selectedBuffs}
          opponentInfo={{
            label: 'Bot',
            level: botLevel,
            energy: botEnergy
          } as OpponentInfo}
          opponentBuffs={[]}
          currentTurn={currentTurn}
          turnTimeLeft={turnTimeLeft}
          turnSubmitted={turnSubmitted}
          waitingForOpponent={waitingForBot}
          battleEnded={battleEnded}
          winner={winner}
          onBackToMenu={onBackToMenu}
        />

            

        {/* AGC: BattleCenter - Reusable battle center component */}
        <BattleCenter
          turnSubmitted={turnSubmitted}
          waitingForOpponent={waitingForBot}
          battleEnded={battleEnded}
          turnTimeLeft={turnTimeLeft}
          selectedChoice={selectedChoice}
          selectedElement={selectedElement}
          onChoiceSelect={(choice) => {
            setSelectedChoice(choice)
            const index = origamiOptions.indexOf(choice)
            if (index >= 0) {
              setChoiceCarouselIndex(index)
            }
            focusSubmitButton()
          }}
          onElementSelect={(element) => {
            setSelectedElement(element)
            const index = elementOptions.indexOf(element)
            if (index >= 0) {
              setElementCarouselIndex(index)
            }
            focusSubmitButton()
          }}
          onSubmitTurn={handleSubmitTurn}
          getChoiceImage={getChoiceImage}
          getElementEmoji={getElementEmoji}
          origamiOptions={origamiOptions}
          elementOptions={elementOptions}
          onPotionSelect={(potionId) => {
            // AGC: Allow toggling potion selection - if null or already selected, deselect; otherwise select
            if (potionId === null || effectivePotion === potionId) {
              // AGC: Deselecting - clear internal state and mark as deselected if it came from prop
              setInternalPotion(null)
              if (propSelectedPotion !== null) {
                setPotionDeselected(true)
              }
            } else {
              // AGC: Selecting - set internal state and clear deselected flag
              setInternalPotion(potionId)
              setPotionDeselected(false)
            }
          }}
          selectedPotion={effectivePotion}
          equippedPotion={equippedPotion}
          potionUsed={potionUsed}
        />

        {/* AGC: BattleLog - Reusable battle log component */}
        <BattleLog
          battleLog={battleLog as BattleLogTurnResult[]}
          getChoiceImage={getChoiceImage}
          getElementEmoji={getElementEmoji}
          opponentLabel="Bot"
        />
                        </div>

    </main>

  )

}
